import React from "react";
import { createPortal } from "react-dom";

// ---------- GanttBarTooltip ----------
// Feb 2026 · Zero-lag custom tooltip that follows the cursor while the
// planner hovers ANY production bar on the Gantt board. A single instance
// is mounted at the parent level and re-positioned via portal — so 50+
// looms rendering hundreds of bars stays cheap (no per-bar tooltip
// subtree). Dark, high-contrast styling readable against every bar hue.
//
// Props:
//   data — null when hidden, else { plan, orderNo, productItems, x, y, kind }
//     productItems = array of { code: string, heads: number }
//       (one per shard sharing the machine window; length 1 for non-split plans).
//     kind = "DOM" | "EXP" chip that flags export vs domestic SO
//   offset — px offset from cursor (default 14x, 18y so pointer never covers)

const OFFSET_X = 14;
const OFFSET_Y = 18;

export default function GanttBarTooltip({ data }) {
  if (!data || typeof document === "undefined") return null;
  const { plan, orderNo, productItems, x, y, kind } = data;
  if (!plan) return null;
  const items = Array.isArray(productItems) && productItems.length ? productItems : [{ code: "—", heads: 0 }];
  const hasHeads = items.some(it => it.heads > 0);

  // Clamp against the viewport so the box never spills off-screen.
  const vw = window.innerWidth || 1024;
  const vh = window.innerHeight || 768;
  const width = 360;
  const height = 100 + items.length * 20;
  const left = Math.min(vw - width - 8, Math.max(8, x + OFFSET_X));
  const top = Math.min(vh - height - 8, Math.max(8, y + OFFSET_Y));

  return createPortal(
    <div
      data-testid="gantt-bar-tooltip"
      style={{ position: "fixed", left, top, width, zIndex: 9999, pointerEvents: "none" }}
      className="rounded-md border border-white/10 bg-zinc-950/95 backdrop-blur-sm shadow-2xl shadow-black/60 text-white text-[11px] leading-snug"
    >
      <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2">
        <span
          data-testid="gantt-tooltip-order"
          className="font-mono font-semibold text-white truncate"
        >
          {orderNo || "—"}
        </span>
        {kind && (
          <span
            data-testid="gantt-tooltip-kind"
            className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm uppercase tracking-widest ${
              kind === "EXP"
                ? "bg-amber-500/20 text-amber-300 border border-amber-400/40"
                : "bg-emerald-500/15 text-emerald-300 border border-emerald-400/40"
            }`}
          >
            {kind}
          </span>
        )}
      </div>
      <div className="px-3 py-2 space-y-1.5">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">
            {items.length > 1 ? `Product Codes · ${items.length} shards` : "Product Code"}
          </div>
          {items.map((it, idx) => (
            <div
              key={`${idx}-${it.code}`}
              className="flex items-baseline gap-2 font-mono text-emerald-300 break-all"
            >
              {hasHeads && (
                <span
                  data-testid={idx === 0 ? "gantt-tooltip-heads" : `gantt-tooltip-heads-${idx}`}
                  className="text-[9px] font-bold px-1 rounded-sm bg-purple-500/25 text-purple-200 border border-purple-400/40 flex-shrink-0"
                  title={`Head-split shard · ${it.heads || "?"} of the machine's heads`}
                >{it.heads || "?"}h</span>
              )}
              <span
                data-testid={idx === 0 ? "gantt-tooltip-product-code" : `gantt-tooltip-product-code-${idx}`}
              >
                {it.code || "—"}
              </span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 font-mono text-[10px] text-zinc-300 pt-0.5">
          <span data-testid="gantt-tooltip-start" className="text-white">{plan.planned_start || "—"}</span>
          <span className="text-zinc-500">→</span>
          <span data-testid="gantt-tooltip-finish" className="text-white">{plan.planned_finish || "—"}</span>
        </div>
      </div>
    </div>,
    document.body
  );
}
